import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import { Text, Heading4 } from 'src/components/ui/typography';
import { Switch } from 'src/components/ui/Swich';
import { BaseInput } from 'src/components/ui/form/BaseInput';
import { SecondaryButton } from '../../ui/Buttons';
import LoadingInline from 'src/components/ui/Loading/LoadingInline';
import useTimeSelector, { QUICK_RANGES } from './useTimeSelector';
import { Tooltip } from 'src/components/ui/Tooltip';
import { Site } from 'src/models';
import { formatDistanceToNow } from 'date-fns';
import { useIsSmallScreen } from 'src/helpers/ui';

interface AnalysisControlsHeaderProps {
  updateDisabled: boolean;
  loading: boolean;
  setIsTimeSelectorOpen: (isOpen: boolean) => void;
  start: Date | undefined;
  end: Date | undefined;
  setStartDate: (start: Date) => void;
  setEndDate: (end: Date) => void;
  selectedQuickRange: string | undefined;
  site: Site;
  lastUpdated: Date | null;
  downloading: boolean;
  tagIds: string[];
  onDownload: () => void;
  triggeredOnly: boolean;
  setTriggeredOnly: (showTriggeredOnly: boolean) => void;
}

const DATE_WITH_TIME = {
  year: 'numeric' as const,
  month: '2-digit' as const,
  day: '2-digit' as const,
  hour: '2-digit' as const,
  minute: '2-digit' as const,
  second: '2-digit' as const,
};
const DATE_FORMAT = {
  year: 'numeric' as const,
  month: '2-digit' as const,
  day: '2-digit' as const,
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
  },
  mask: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    backgroundColor: 'transparent',
    cursor: 'pointer',
    border: 'none',
  },
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    width: '100%',
    justifyContent: 'flex-end',
    minWidth: '272px',
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    width: '100%',
  },
  dateInputWrapper: {
    position: 'relative',
    width: 'auto',
    minWidth: '206px',
  },
  smallScreenContainer: {
    flexDirection: 'column',
  },
  smallScreenLeftSection: {
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
  smallScreenRightSection: {
    alignItems: 'flex-start',
    flexDirection: 'column-reverse',
  },
  smallScreenSwitchContainer: {
    justifyContent: 'space-between',
  },
  smallScreenDateInputWrapper: {
    width: '100%',
  },
}));

const AnalysisControlsHeader: React.FC<AnalysisControlsHeaderProps> = ({
  updateDisabled,
  loading,
  setIsTimeSelectorOpen,
  start,
  end,
  setStartDate,
  setEndDate,
  selectedQuickRange,
  site,
  lastUpdated,
  downloading,
  tagIds,
  onDownload,
  triggeredOnly,
  setTriggeredOnly,
}) => {
  const isSmallScreen = useIsSmallScreen();
  const classes = useStyles();
  const { refreshTimeRange } = useTimeSelector({
    start,
    end,
    setStartDate,
    setEndDate,
    loading,
    selectedQuickRange,
    setSelectedQuickRange: () => {},
  });

  const handleUpdateDates = () => {
    refreshTimeRange();
  };

  const inputValue = useMemo(() => {
    if (selectedQuickRange) {
      return {
        label: QUICK_RANGES.find((range) => range.key === selectedQuickRange)?.label,
        tooltip: QUICK_RANGES.find((range) => range.key === selectedQuickRange)?.label,
      };
    }
    return {
      label:
        start?.toLocaleString(undefined, DATE_FORMAT) +
        ' - ' +
        end?.toLocaleString(undefined, DATE_FORMAT),
      tooltip:
        start?.toLocaleString(undefined, DATE_WITH_TIME) +
        ' - ' +
        end?.toLocaleString(undefined, DATE_WITH_TIME),
    };
  }, [selectedQuickRange, start, end]);

  const handleInputClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsTimeSelectorOpen(true);
  };

  return (
    <div className={`${classes.container} ${isSmallScreen ? classes.smallScreenContainer : ''}`}>
      <div
        className={`${classes.leftSection} ${isSmallScreen ? classes.smallScreenLeftSection : ''}`}
      >
        <div>
          <Heading4>{site.name}</Heading4>
          {selectedQuickRange && lastUpdated && (
            <Text fontSize={12}>
              Last updated: {formatDistanceToNow(lastUpdated, { addSuffix: true })}
            </Text>
          )}
        </div>
        <SecondaryButton
          disabled={downloading || tagIds.length === 0 || loading}
          onClick={onDownload}
        >
          Download CSV {downloading && <LoadingInline />}
        </SecondaryButton>
      </div>
      <div
        className={`${classes.rightSection} ${
          isSmallScreen ? classes.smallScreenRightSection : ''
        }`}
      >
        <div
          className={`${classes.switchContainer} ${
            isSmallScreen ? classes.smallScreenSwitchContainer : ''
          }`}
        >
          <Text>Show only triggered faults</Text>
          <Switch checked={triggeredOnly} onChange={(e) => setTriggeredOnly(e.target.checked)} />
        </div>
        <div className={classes.inputContainer}>
          <Tooltip title={inputValue.tooltip ?? ''}>
            <div
              className={`${classes.dateInputWrapper} ${
                isSmallScreen ? classes.smallScreenDateInputWrapper : ''
              }`}
            >
              <button className={classes.mask} onClick={handleInputClick}></button>
              <BaseInput value={inputValue.label} size="small" fullWidth />
            </div>
          </Tooltip>
          {selectedQuickRange && (
            <SecondaryButton
              disabled={updateDisabled}
              onClick={handleUpdateDates}
              startIcon={loading && <LoadingInline size={14} />}
            >
              Refresh
            </SecondaryButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalysisControlsHeader;
